// ArticleA5.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const THEME = {
  fontFamily:
    "'Prompt', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans Thai', sans-serif",
  pageBg: "#0b1220",
  textOnDark: "rgba(255,255,255,.92)",
  glass: "rgba(255,255,255,.08)",
  glassStroke: "rgba(255,255,255,.18)",
  primary: "#60a5fa",
  primary2: "#22d3ee",
};

const META = {
  id: "a5",
  title: "Checklist ทวงหนี้สำหรับธุรกิจบริการรายเดือน",
  category: "ทวงหนี้",
  dateISO: "2025-04-25", // 25 เม.ย. 2568 (จะแสดงแบบไทย)
  readMins: 4, // ล็อกตามสเปก
  hero:
    "https://images.unsplash.com/photo-1554224154-22dec7ec8818?q=80&w=2000&auto=format&fit=crop",
  excerpt:
    "จากออกบิล → แจ้งเตือน → หนังสือทวงถาม → ส่งทนายความ: ทำทีละขั้น ปิดหนี้เร็วขึ้น",
};

export default function ArticleA5() {
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState("");
  const contentRef = useRef(null);
  const heroRef = useRef(null);

  // progress + hero parallax
  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const total = el.scrollHeight - window.innerHeight * 0.6;
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - window.innerHeight * 0.4), 0),
        total
      );
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);

      const h = heroRef.current;
      if (h) {
        const y = window.scrollY;
        h.style.setProperty("--parallax", Math.min(y * 0.22, 120) + "px");
        h.style.setProperty("--blur", Math.min(y / 70, 8) + "px");
        h.style.setProperty("--fade", Math.min(y / 420, 0.5));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ScrollSpy
  useEffect(() => {
    const heads = document.querySelectorAll("main article h2[id], main article h3[id]");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActiveId(e.target.id)),
      { rootMargin: "0px 0px -70% 0px", threshold: 0.01 }
    );
    heads.forEach((h) => io.observe(h));
    return () => io.disconnect();
  }, []);

  const url = useMemo(
    () => (typeof window !== "undefined" ? window.location.href : "/articles/a5"),
    []
  );

  const share = (type) => {
    const text = encodeURIComponent(META.title);
    const u = encodeURIComponent(url);
    let shareUrl = "";
    if (type === "fb") shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    if (type === "tw") shareUrl = `https://twitter.com/intent/tweet?url=${u}&text=${text}`;
    if (type === "ln") shareUrl = `https://social-plugins.line.me/lineit/share?url=${u}`;
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=680,height=520");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      alert("คัดลอกลิงก์แล้ว");
    } catch {
      alert("คัดลอกไม่สำเร็จ");
    }
  };
  const copyTemplate = (id, label) => {
    const t = document.getElementById(id)?.innerText || "";
    navigator.clipboard
      .writeText(t)
      .then(() => alert(`คัดลอก${label}แล้ว`))
      .catch(() => alert("คัดลอกไม่สำเร็จ"));
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <div className="article-page" style={{ fontFamily: THEME.fontFamily }}>
      <Style />
      <ReadingProgress value={progress} />

      {/* SEO JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: META.title,
            datePublished: META.dateISO,
            dateModified: META.dateISO,
            author: { "@type": "Person", name: "ทีมทนายความ Thai Tax & Law" },
            image: META.hero,
            articleSection: META.category,
            description: META.excerpt,
            mainEntityOfPage: url,
          }),
        }}
      />

      {/* HERO */}
      <header className="hero" ref={heroRef}>
        <img className="hero-media" src={META.hero} alt="" />
        <div className="hero-overlay" />
        <div className="hero-content">
          <nav className="breadcrumbs">
            <a href="/">หน้าแรก</a> <span>•</span> <a href="/articles">บทความ</a>{" "}
            <span>•</span> <span aria-current="page">{META.category}</span>
          </nav>
          <h1 className="hero-title">{META.title}</h1>
          <p className="hero-excerpt">{META.excerpt}</p>
          <div className="hero-meta">
            <span>{fmtDate(META.dateISO)}</span>
            <span>•</span>
            <span>{META.readMins} นาที</span>
            <span className="cat">{META.category}</span>
          </div>
        </div>
        <div className="hero-bottom-fade" />
      </header>

      {/* LAYOUT */}
      <div className="containerr">
        {/* SIDE */}
        <aside className="side tools">
          <div className="tool-card">
            <div className="tool-title">สารบัญ</div>
            <nav className="toc">
              {[
                ["overview", "ภาพรวม & เป้าหมาย"],
                ["kpi", "KPI ที่ควรติดตาม"],
                ["setup", "ตั้งต้นให้พร้อม (สัญญา/บิล)"],
                ["flow", "Flow รายเดือน 4 ขั้น (ทำตามเช็กลิสต์)"],
                ["templates", "ข้อความพร้อมใช้ (อีเมล/ไลน์/SMS)"],
                ["demand", "เทมเพลตหนังสือทวงถาม"],
                ["docs", "เอกสารและหลักฐานที่ต้องเก็บ"],
                ["negotiate", "ตัวเลือกปิดบัญชี & ผ่อนชำระ"],
                ["automation", "ไอเดียออโตเมชัน"],
                ["faq", "FAQ"],
              ].map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={activeId === id ? "active" : ""}
                  onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(id);
                if (el) {
                    const yOffset = -80; // ชดเชย navbar 80px
                    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                }
                }}

                >
                  {label}
                </a>
              ))}
            </nav>
          </div>

          
        </aside>

        {/* MAIN */}
        <main>
          <article ref={contentRef}>
            <p className="lede">
              สำหรับธุรกิจบริการรายเดือน (เช่น SaaS, Outsource บัญชี/การตลาด, สัญญาบริการรายเดือน)
              การทวงหนี้ต้อง<b>เป็นระบบและมีเส้นตายชัด</b> เพื่อปิดบัญชีให้เร็ว โดยไม่เสียความสัมพันธ์ที่จำเป็น
            </p>

            <Callout type="info">
              บทความนี้เป็นแนวทางทั่วไป โปรดปรับใช้ให้เข้ากับสัญญา/อุตสาหกรรมของคุณ
              หากเป็นเคสอ่อนไหว/มูลค่าสูง <a href="/contact">ปรึกษาทนายความ</a> ก่อนดำเนินการ
            </Callout>

            <h2 id="overview">ภาพรวม & เป้าหมาย</h2>
            <ul className="check">
              <li>ตั้งกระบวนการจาก <b>ออกบิล → แจ้งเตือน → หนังสือทวงถาม → ส่งทนายความ</b></li>
              <li>ให้ลูกค้ามี<b>ทางเลือกชำระ</b> ที่ครบ (โอน/บัตร/พร้อมเพย์/ชำระอัตโนมัติ)</li>
              <li>เก็บร่องรอยทุกครั้งเพื่อรองรับภาษี/คดี</li>
            </ul>

            <h2 id="kpi">KPI ที่ควรติดตาม</h2>
            <div className="grid-2">
              <ul className="check">
                <li><b>DSO</b> (Days Sales Outstanding)</li>
                <li><b>Collection Rate</b> รายเดือน</li>
                <li>% ลูกหนี้ค้าง <b>0–30/31–60/61–90/90+ วัน</b></li>
              </ul>
              <ul className="check">
                <li>เวลาตอบกลับลูกค้าค้างชำระ <b>ภายใน 24 ชม.</b></li>
                <li>อัตราปิดหนี้หลัง <b>รอบเตือน 1 / รอบเตือน 2</b></li>
                <li>จำนวนคดี/มูลค่าที่ต้องส่งทนายความต่อเดือน</li>
              </ul>
            </div>

            <h2 id="setup">ตั้งต้นให้พร้อม (สัญญา/บิล)</h2>
            <ul>
              <li>สัญญาระบุ <b>วันตัดรอบ/กำหนดชำระ/ดอกเบี้ยผิดนัด</b> ชัดเจน</li>
              <li>ระบุ <b>ช่องทางติดต่อการเงิน</b> เฉพาะ (อีเมล/ไลน์/โทร)</li>
              <li>ออกใบแจ้งหนี้พร้อม <b>QR/ลิงก์จ่าย</b> และ <b>PO/Ref</b> ของลูกค้า</li>
              <li>ตั้ง <b>ปฏิทินตัดรอบ</b> และ Automation การส่งบิล</li>
            </ul>

            <h2 id="flow">Flow รายเดือน 4 ขั้น (ทำตามเช็กลิสต์)</h2>
            <div className="glass-list">
              <h3>Step 0 — ออกบิล (Day 0)</h3>
              <ul className="check">
                <li>ส่งบิล + วิธีจ่าย + เส้นตาย (เช่น วันที่ 10 ของเดือน)</li>
                <li>แนบสัญญา/ใบสั่งซื้อ/สรุปชั่วโมงงาน (ถ้ามี)</li>
                <li>ตั้ง Reminder อัตโนมัติ Day +3 / +7 / +14</li>
              </ul>

              <h3>Step 1 — แจ้งเตือนรอบ 1 (Day +3)</h3>
              <ul className="check">
                <li>แจ้งสุภาพ–เป็นมิตร ขอคอนเฟิร์มวันโอน</li>
                <li>ย้ำช่องทางจ่ายและผู้ติดต่อ</li>
              </ul>

              <h3>Step 2 — แจ้งเตือนรอบ 2 (Day +7)</h3>
              <ul className="check">
                <li>ระบุยอด + ดอกเบี้ย(ถ้ามี) + <b>เส้นตายสุดท้าย 7 วัน</b></li>
                <li>แจ้งว่าจะออก <b>หนังสือทวงถาม</b> หากพ้นกำหนด</li>
              </ul>

              <h3>Step 3 — หนังสือทวงถาม (Day +14)</h3>
              <ul className="check">
                <li>ส่งทางที่พิสูจน์ได้: ไปรษณีย์ลงทะเบียน/อีเมลบริษัท + CC ผู้บริหาร</li>
                <li>แนบสัญญา/บิล/หลักฐานส่งงาน</li>
                <li>ให้เวลา 7–14 วัน พร้อมระบุขั้นตอนถัดไป</li>
              </ul>

              <h3>Step 4 — ส่งทนายความ (Day +21)</h3>
              <ul className="check">
                <li>ส่งแฟ้มเอกสารครบชุด (ดูหัวข้อ “เอกสารและหลักฐาน”)</li>
                <li>พิจารณา <b>ยุติสัญญา/ระงับบริการ</b> ตามเงื่อนไขในสัญญา</li>
              </ul>
            </div>

            <h2 id="templates">ข้อความพร้อมใช้ (อีเมล/ไลน์/SMS)</h2>
            <div className="template">
              <pre id="msg-soft">{`[แจ้งเตือนรอบ 1 – โทนอ่อน]
สวัสดีค่ะ/ครับ คุณ_____
ขอแจ้งเตือนการชำระค่าบริการรอบเดือน ______ เลขที่บิล ______ ยอด ______ บาท กำหนดชำระ __/__/____
สามารถชำระผ่าน ______ และแจ้งสลิปที่อีเมล/ไลน์นี้ได้เลยค่ะ ขอบคุณมากค่ะ`}</pre>
              <div className="template-actions">
                <button onClick={() => copyTemplate("msg-soft", "ข้อความรอบ 1")}>คัดลอกข้อความรอบ 1</button>
              </div>
            </div>
            <div className="template" style={{marginTop:12}}>
              <pre id="msg-hard">{`[แจ้งเตือนรอบ 2 – มีเส้นตาย]
เรียน คุณ_____
ตามบิล ______ (เดือน ______) ยอด ______ บาท เกินกำหนดแล้ว
โปรดชำระภายใน __/__/____ หากพ้นกำหนดทางเราจะออก “หนังสือทวงถาม” ตามขั้นตอน
ชำระผ่าน ______ และแจ้งการชำระได้ที่ ______ ขอบคุณครับ/ค่ะ`}</pre>
              <div className="template-actions">
                <button onClick={() => copyTemplate("msg-hard", "ข้อความรอบ 2")}>คัดลอกข้อความรอบ 2</button>
              </div>
            </div>

            <h2 id="demand">เทมเพลตหนังสือทวงถาม</h2>
            <div className="template">
              <pre id="demand-1">{`หนังสือทวงถาม (Demand Letter) — บริการรายเดือน รอบที่ 1
วันที่ ___/___/____

เรื่อง แจ้งยอดคงค้างและกำหนดชำระครั้งสุดท้าย
เรียน บริษัท/คุณ __________________

ตามสัญญาบริการเลขที่ ______ รอบเดือน ______ ได้ออกใบแจ้งหนี้เลขที่ ______ ยอด ______ บาท กำหนดชำระ __/__/____
โปรดชำระภายใน __/__/____ โดยโอนเข้าบัญชี __________ หรือช่องทาง __________ และแจ้งหลักฐานการชำระ

หากพ้นกำหนด บริษัทจำเป็นต้องพิจารณาระงับบริการชั่วคราวและดำเนินการตามกฎหมายต่อไป
ขอแสดงความนับถือ
บริษัท __________________
ผู้ติดต่อ __________________ โทร __________________`}</pre>
              <div className="template-actions">
                <button onClick={() => copyTemplate("demand-1", "เทมเพลตหนังสือทวงถามรอบ 1")}>คัดลอก</button>
              </div>
            </div>

            <div className="template" style={{marginTop:12}}>
              <pre id="demand-2">{`หนังสือทวงถาม (Final Notice) — ก่อนส่งทนายความ
วันที่ ___/___/____

เรียน บริษัท/คุณ __________________
ทางเราได้ทวงถามหลายครั้งแล้วตามเอกสารอ้างถึง แต่ยังไม่ได้รับชำระยอด ______ บาท (บิลเลขที่ ______ เดือน ______)
โปรดชำระภายใน __/__/____ มิฉะนั้นบริษัทจะมอบหมายให้ทนายความดำเนินคดีและ/หรือเรียกค่าเสียหายตามสัญญา

ยังเปิดโอกาสให้เสนอแผนผ่อนชำระได้ โปรดติดต่อ __________________
ขอแสดงความนับถือ
บริษัท __________________
ผู้ติดต่อ __________________ โทร __________________`}</pre>
              <div className="template-actions">
                <button onClick={() => copyTemplate("demand-2", "Final Notice")}>คัดลอก</button>
                <a className="ghost" href="/contact">อยากให้เราจัดทำเอกสารบนหัวกระดาษบริษัทคุณ</a>
              </div>
            </div>

            <h2 id="docs">เอกสารและหลักฐานที่ต้องเก็บ</h2>
            <ul className="check">
              <li>สัญญาบริการ/ใบเสนอราคา/PO</li>
              <li>ใบแจ้งหนี้/ใบกำกับภาษี/ใบส่งงาน</li>
              <li>log การส่งอีเมล/ไลน์, หลักฐานอ่าน/ตอบ, ใบรับไปรษณีย์</li>
              <li>บันทึกการโทร (วัน–เวลา–ผู้คุย–สรุป)</li>
              <li>หลักฐานการหยุดให้บริการ (ถ้ามีระงับ)</li>
            </ul>

            <h2 id="negotiate">ตัวเลือกปิดบัญชี & ผ่อนชำระ</h2>
            <ul>
              <li>ส่วนลดเมื่อชำระภายใน X วัน</li>
              <li>ผ่อน 2–3 งวด พร้อม <b>หนังสือยอมรับหนี้</b> และระบุ <b>ดอกเบี้ยผิดนัด</b></li>
              <li>แลกเปลี่ยนบริการ/เครดิตอนาคต (ถ้าคุ้มค่ากว่าเงินสด)</li>
            </ul>

            <h2 id="automation">ไอเดียออโตเมชัน</h2>
            <div className="grid-2">
              <ul className="check">
                <li>ตั้ง Workflow: Day 0 ส่งบิล, +3 เตือน, +7 เตือนรอบ 2, +14 สร้างจดหมายอัตโนมัติ</li>
                <li>เชื่อม Payment Link + ออกใบเสร็จอัตโนมัติ</li>
              </ul>
              <ul className="check">
                <li>ติดป้ายกำกับลูกหนี้: <code>0–30 / 31–60 / 61–90 / 90+</code></li>
                <li>แดชบอร์ด KPI: DSO, Collection Rate, Aging</li>
              </ul>
            </div>

            <h2 id="faq">FAQ</h2>
            <details>
              <summary>ต้องส่งหนังสือทวงถามกี่ครั้ง?</summary>
              <p>อย่างน้อย 1 ครั้งแบบพิสูจน์ได้ (ลงทะเบียน/อีเมลทางการ) ก่อนส่งทนาย เหตุผลหลักคือสร้างร่องรอยและให้โอกาสสุดท้าย</p>
            </details>
            <details>
              <summary>จะหยุดให้บริการได้เมื่อไร?</summary>
              <p>ยึดตามสัญญา หากกำหนดสิทธิระงับ/ยุติไว้ชัดเจน ให้แจ้งล่วงหน้าและเก็บหลักฐานการแจ้ง</p>
            </details>

            <Callout type="success">
              อยากให้ทีมเราตั้ง Flow/เทมเพลตให้ทั้งระบบ?{" "}
              <a href="/contact">นัดคุยฟรี 15 นาที</a>
            </Callout>

            <div className="end-actions">
              <a className="btn-primary" href="/contact">
                ให้ทีมทนายความช่วยวางกระบวนการ
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M5 12h12M13 5l7 7-7 7" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a
                className="ghost"
                href="#top"
                onClick={(e) => {e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" });}}
              >
                กลับขึ้นบน
              </a>
            </div>
          </article>

          {/* RELATED */}
          <section className="related">
            <h3>อ่านต่อ</h3>
            <div className="rel-grid">
              <a className="rel-card" href="/articles/a1">
                <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta"><span className="cat">ทวงหนี้</span><div className="title">คู่มือการติดตามทวงหนี้อย่างถูกกฎหมาย</div></div>
              </a>
              <a className="rel-card" href="/articles/a4">
                <img src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta"><span className="cat">ภาษี</span><div className="title">ภาษีหนี้สูญ: ตัดหนี้ให้ถูกต้อง</div></div>
              </a>
              <a className="rel-card" href="/articles/a3">
                <img src="https://images.unsplash.com/photo-1542621334-a254cf47733d?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta"><span className="cat">สัญญา</span><div className="title">สัญญากู้ยืมเงิน: เขียนให้ฟ้องได้</div></div>
              </a>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

/* ---------- Small Components ---------- */
function ReadingProgress({ value }) {
  return (
    <div className="progress-wrap" aria-hidden="true">
      <div className="progress" style={{ width: `${value}%` }} />
    </div>
  );
}
function Callout({ type = "info", children }) {
  return <div className={`callout ${type}`}>{children}</div>;
}

/* ---------- Styles ---------- */
function Style() {
  return (
    <style>{`
:root{
  --bg:${THEME.pageBg}; --text:${THEME.textOnDark};
  --glass:${THEME.glass}; --glass-stroke:${THEME.glassStroke};
  --p1:${THEME.primary}; --p2:${THEME.primary2};
}
*{box-sizing:border-box}
.article-page{
  color:var(--text);
  background:
    radial-gradient(1200px 600px at 10% -10%, rgba(14,165,233,.12), transparent 60%),
    radial-gradient(1200px 600px at 90% 10%, rgba(99,102,241,.12), transparent 60%),
    var(--bg);
  min-height:100vh;
}
/* progress */
.progress-wrap{position:fixed;top:0;left:0;right:0;height:3px;z-index:100}
.progress{height:3px;background:linear-gradient(90deg,var(--p1),var(--p2));box-shadow:0 0 16px rgba(34,211,238,.35)}
/* hero */
.hero{position:relative;height:58vh;overflow:hidden;isolation:isolate;--parallax:0px;--blur:0px;--fade:0}
.hero-media{position:absolute;inset:-10% -10% -10% -10%;width:120%;height:120%;object-fit:cover;transform:translateY(var(--parallax));filter:blur(var(--blur)) brightness(.85) saturate(.95)}
.hero-overlay{position:absolute;inset:0;background:
  radial-gradient(700px 380px at 18% 32%, rgba(0,0,0,.45), rgba(0,0,0,.7)),
  linear-gradient(90deg, rgba(0,0,0,.6), rgba(0,0,0,.25), rgba(0,0,0,.6));opacity:calc(1 - var(--fade)*.35)}
.hero-bottom-fade{position:absolute;left:0;right:0;bottom:-1px;height:120px;background:linear-gradient(to bottom, transparent, var(--bg));z-index:2}
.hero-content{position:relative;z-index:3;height:100%;display:grid;align-content:center;gap:14px;padding: clamp(20px,5vw,64px);max-width:1100px;margin:0 auto}
.breadcrumbs{opacity:.9;font-size:.95rem}
.breadcrumbs a{color:rgba(255,255,255,.9);text-decoration:none}
.breadcrumbs span{opacity:.6;margin:0 6px}
.hero-title{font-size: clamp(2rem, 4.2vw, 3.6rem);line-height:1.1;margin:0;font-weight:800}
.hero-excerpt{max-width:60ch;opacity:.98}
.hero-meta{display:flex;gap:8px;align-items:center;opacity:.9}
.hero-meta .cat{margin-left:8px;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.2)}
/* layout */
.containerr{display:grid;grid-template-columns:300px 1fr;gap:24px;max-width:1200px;margin:0 auto;padding:0 clamp(16px,4vw,24px)}
.side{padding-top:28px}
main{padding-top:28px}
/* tools */
.tool-card{position:sticky;top:84px;background:var(--glass);border:1px solid var(--glass-stroke);backdrop-filter: blur(8px);border-radius:16px;padding:14px 14px 12px;margin-bottom:14px}
.tool-title{font-weight:800;margin-bottom:8px}
.toc{display:grid;gap:8px}
.toc a{padding:8px 10px;border-radius:12px;text-decoration:none;color:var(--text);border:1px solid transparent}
.toc a:hover{background:rgba(255,255,255,.08)}
.toc a.active{border-color:rgba(255,255,255,.25);background:linear-gradient(90deg, rgba(96,165,250,.28), rgba(34,211,238,.28))}
.share .share-row{display:grid;gap:8px}
.share button{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);color:var(--text);cursor:pointer}
.share button:hover{background:rgba(255,255,255,.12)}
.author-meta{opacity:.85}
/* article */
article{max-width:760px;margin:0 auto 36px}
.lede{font-size:1.1rem;opacity:.98}
h2{font-size:1.6rem;margin:28px 0 12px;font-weight:900}
h3{font-size:1.15rem;margin:14px 0 6px;font-weight:800}
p{line-height:1.8}
ul{padding-left:18px}
.grid-2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.check li::marker{content:"✔  "}
.callout{border-radius:16px;padding:14px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);margin:14px 0}
.callout.info{background:linear-gradient(180deg, rgba(96,165,250,.15), rgba(255,255,255,.06))}
.callout.success{background:linear-gradient(180deg, rgba(52,211,153,.18), rgba(255,255,255,.06))}
.glass-list{background:var(--glass);border:1px solid var(--glass-stroke);backdrop-filter: blur(8px);border-radius:16px;padding:12px}
.template{border:1px solid var(--glass-stroke);border-radius:16px;overflow:hidden;background:rgba(255,255,255,.04);margin-top:8px}
.template pre{margin:0;padding:14px;line-height:1.7;white-space:pre-wrap}
.template-actions{display:flex;gap:10px;align-items:center;padding:10px;border-top:1px solid var(--glass-stroke)}
.template-actions .ghost{color:var(--text);opacity:.9;text-decoration:none}
.template-actions button{padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.08);color:var(--text);cursor:pointer}
.template-actions button:hover{background:rgba(255,255,255,.14)}
/* end actions */
.end-actions{display:flex;gap:10px;align-items:center;margin:18px 0 6px}
.btn-primary{display:inline-flex;align-items:center;gap:10px;padding:12px 16px;border-radius:999px;color:#fff;text-decoration:none;background:linear-gradient(90deg,var(--p1),var(--p2));border:1px solid rgba(255,255,255,.25)}
.ghost{padding:10px 12px;border-radius:12px;border:1px solid var(--glass-stroke);text-decoration:none;color:var(--text);background:rgba(255,255,255,.06)}
/* related */
.related{border-top:1px solid rgba(255,255,255,.08);padding-top:20px}
.related h3{font-size:1.2rem;margin:0 0 12px}
.rel-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.rel-card{position:relative;overflow:hidden;border-radius:16px;border:1px solid rgba(255,255,255,.12);text-decoration:none;color:#fff;background:#0f1629}
.rel-card img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;filter:brightness(.9);transform:scale(1.02);transition:transform .5s}
.rel-card:hover img{transform:scale(1.06)}
.rel-card .meta{position:relative;padding:12px;background:linear-gradient(180deg, rgba(0,0,0,.0), rgba(0,0,0,.75));margin-top:110px}
.rel-card .cat{display:inline-block;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28);margin-bottom:6px}
.rel-card .title{font-weight:800}
/* responsive */
@media (max-width: 1024px){
  .containerr{grid-template-columns:1fr}
  .side{order:2}
  article{max-width:680px}
}
@media (max-width:640px){
  .hero{height:54vh}
  .grid-2{grid-template-columns:1fr}
  .rel-grid{grid-template-columns:1fr}
}
    `}</style>
  );
}
